import { base64ToBytes, bytesToBase64, randomBytes, toUint8Array, utf8ToBytes } from './bytes.js';

export const KDF_ARGON2ID = 'argon2id';
export const KDF_PBKDF2 = 'pbkdf2';
export const PBKDF2_MIN_ITERATIONS = 600000;
export const KDF_KEY_LENGTH_BYTES = 32;
export const KDF_MIN_SALT_BYTES = 16;

function getSubtleCrypto() {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error('WebCrypto subtle API is unavailable.');
  }
  return subtle;
}

export function isArgon2idAvailable() {
  return Boolean(globalThis.argon2id || globalThis.argon2?.hash);
}

function normalizeKdfName(value) {
  const name = (value || KDF_PBKDF2).toLowerCase();
  if (name !== KDF_ARGON2ID && name !== KDF_PBKDF2) {
    throw new Error(`Unsupported KDF: ${value}`);
  }
  return name;
}

function normalizeSalt(salt, saltB64) {
  if (salt !== undefined && salt !== null) {
    const bytes = toUint8Array(salt, 'salt');
    if (bytes.length < KDF_MIN_SALT_BYTES) {
      throw new Error(`salt must be at least ${KDF_MIN_SALT_BYTES} bytes.`);
    }
    return bytes;
  }

  if (typeof saltB64 === 'string' && saltB64) {
    const bytes = base64ToBytes(saltB64, 'saltB64');
    if (bytes.length < KDF_MIN_SALT_BYTES) {
      throw new Error(`salt must be at least ${KDF_MIN_SALT_BYTES} bytes.`);
    }
    return bytes;
  }

  return randomBytes(KDF_MIN_SALT_BYTES);
}

function normalizePbkdf2Iterations(value) {
  const iterations = Number(value ?? PBKDF2_MIN_ITERATIONS);
  if (!Number.isInteger(iterations) || iterations < PBKDF2_MIN_ITERATIONS) {
    throw new Error(`PBKDF2 iterations must be >= ${PBKDF2_MIN_ITERATIONS}.`);
  }
  return iterations;
}

async function deriveWithPbkdf2(passphrase, saltBytes, iterations, keyLengthBytes) {
  const subtle = getSubtleCrypto();
  const passphraseBytes = utf8ToBytes(passphrase);
  const baseKey = await subtle.importKey(
    'raw',
    passphraseBytes,
    { name: 'PBKDF2' },
    false,
    ['deriveBits']
  );
  const bits = await subtle.deriveBits(
    {
      name: 'PBKDF2',
      hash: 'SHA-256',
      salt: saltBytes,
      iterations,
    },
    baseKey,
    keyLengthBytes * 8
  );
  return new Uint8Array(bits);
}

function normalizeArgon2Result(value) {
  if (!value) {
    throw new Error('Argon2id provider returned empty result.');
  }
  if (value instanceof Uint8Array || value instanceof ArrayBuffer || ArrayBuffer.isView(value)) {
    return toUint8Array(value, 'argon2Result');
  }
  if (typeof value.hashHex === 'string') {
    const hex = value.hashHex;
    if (hex.length % 2 !== 0) {
      throw new Error('Invalid Argon2id hex output.');
    }
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = Number.parseInt(hex.slice(i, i + 2), 16);
    }
    return bytes;
  }
  if (typeof value.hash === 'string') {
    return base64ToBytes(value.hash, 'argon2Result.hash');
  }
  if (value.hash instanceof Uint8Array || value.hash instanceof ArrayBuffer || ArrayBuffer.isView(value.hash)) {
    return toUint8Array(value.hash, 'argon2Result.hash');
  }
  throw new Error('Unsupported Argon2id provider result shape.');
}

async function deriveWithArgon2id(passphrase, saltBytes, iterations, keyLengthBytes, memoryKib, parallelism) {
  const argon2idProvider = globalThis.argon2id;
  if (typeof argon2idProvider === 'function') {
    const result = await argon2idProvider({
      passphrase,
      salt: saltBytes,
      iterations,
      memoryKib,
      parallelism,
      hashLength: keyLengthBytes,
      outputType: 'binary',
    });
    return normalizeArgon2Result(result);
  }

  const argon2Provider = globalThis.argon2;
  if (typeof argon2Provider?.hash === 'function') {
    const result = await argon2Provider.hash({
      pass: passphrase,
      salt: saltBytes,
      time: iterations,
      mem: memoryKib,
      parallelism,
      hashLen: keyLengthBytes,
      type: argon2Provider.ArgonType?.Argon2id ?? 2,
      raw: true,
    });
    return normalizeArgon2Result(result);
  }

  throw new Error('Argon2id provider is unavailable.');
}

export async function derivePassphraseKey({
  passphrase,
  kdf = {},
  keyLengthBytes = KDF_KEY_LENGTH_BYTES,
  allowFallback = true,
}) {
  if (typeof passphrase !== 'string' || passphrase.length < 1) {
    throw new Error('passphrase is required.');
  }
  if (!Number.isInteger(keyLengthBytes) || keyLengthBytes <= 0) {
    throw new Error('keyLengthBytes must be a positive integer.');
  }

  const name = normalizeKdfName(kdf.name);
  const saltBytes = normalizeSalt(kdf.salt, kdf.salt_b64);

  if (name === KDF_ARGON2ID) {
    const iterations = Number(kdf.iterations ?? 3);
    const memoryKib = Number(kdf.memory_kib ?? 65536);
    const parallelism = Number(kdf.parallelism ?? 1);

    try {
      const keyBytes = await deriveWithArgon2id(
        passphrase,
        saltBytes,
        iterations,
        keyLengthBytes,
        memoryKib,
        parallelism
      );

      return {
        keyBytes,
        kdfUsed: {
          name: KDF_ARGON2ID,
          iterations,
          memory_kib: memoryKib,
          parallelism,
          salt_b64: bytesToBase64(saltBytes),
        },
      };
    } catch (error) {
      if (!allowFallback) {
        throw error;
      }
    }

    const pbkdf2FallbackIterations = normalizePbkdf2Iterations(
      kdf.pbkdf2_iterations ?? PBKDF2_MIN_ITERATIONS
    );
    const fallbackKeyBytes = await deriveWithPbkdf2(
      passphrase,
      saltBytes,
      pbkdf2FallbackIterations,
      keyLengthBytes
    );
    return {
      keyBytes: fallbackKeyBytes,
      kdfUsed: {
        name: KDF_PBKDF2,
        iterations: pbkdf2FallbackIterations,
        salt_b64: bytesToBase64(saltBytes),
      },
    };
  }

  const pbkdf2Iterations = normalizePbkdf2Iterations(kdf.iterations);
  const keyBytes = await deriveWithPbkdf2(passphrase, saltBytes, pbkdf2Iterations, keyLengthBytes);
  return {
    keyBytes,
    kdfUsed: {
      name: KDF_PBKDF2,
      iterations: pbkdf2Iterations,
      salt_b64: bytesToBase64(saltBytes),
    },
  };
}
